"use strict";
/**
 * @packageDocumentation
 * @module index
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.wrap = exports.try = exports.tryCatch = exports.filter = exports.each = exports.map = exports.runTimeout = exports.timeout = exports.TimeoutRunner = exports.delay = exports.TimeoutError = exports.defer = exports.makeDefer = exports.Defer = void 0;
const tslib_1 = require("tslib");
/* eslint-disable max-statements, @typescript-eslint/ban-types */
const assert_1 = tslib_1.__importDefault(require("assert"));
const util_1 = require("util");
const setTimeoutPromise = util_1.promisify(setTimeout);
/**
 * Defer object for fulfilling a promise later in other events
 *
 * To use, use `xaa.makeDefer` or its alias `xaa.defeer`.
 *
 */
class Defer {
    /**
     * construct Defer
     *
     * @param ThePromise optional promise constructor
     */
    constructor(ThePromise = global.Promise) {
        this.promise = new ThePromise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
    /**
     * node.js callback for the deferred promise
     *
     * @param err - error
     * @param args - result
     * @returns nothing
     */
    done(err, ...args) {
        // Not declaring (err, result) explicitly for standard node.js callback.
        // we can't be sure if user's API expects callback that
        // should have a second arg for result.
        if (err)
            this.reject(err);
        else
            this.resolve(args[0]);
    }
}
exports.Defer = Defer;
/**
 * Create a promise Defer object
 *
 * Sample:
 *
 * ```js
 * async function waitEvent() {
 *   const defer = xaa.makeDefer();
 *   someThing.on("event", (data) => defer.resolve(data))
 *   return defer.promise;
 * }
 * ```
 *
 * @param Promise - optional Promise constructor.
 * @returns Defer instance
 */
function makeDefer(Promise = global.Promise) {
    return new Defer(Promise);
}
exports.makeDefer = makeDefer;
exports.defer = makeDefer;
/**
 * The error xaa.timeout will throw if operation timed out
 */
class TimeoutError extends Error {
    constructor(msg) {
        super(msg);
        this.name = "TimeoutError";
        assert_1.default(msg);
    }
}
exports.TimeoutError = TimeoutError;
async function delay(delayMs, valOrFunc) {
    await setTimeoutPromise(delayMs);
    return typeof valOrFunc === "function" ? /* lazily */ valOrFunc() : valOrFunc;
}
exports.delay = delay;
/**
 * TimeoutRunner for running tasks (promises) with a timeout
 *
 * Please use `xaa.timeout` or `xaa.runTimeout` APIs instead.
 */
class TimeoutRunner {
    constructor(maxMs, rejectMsg) {
        this.maxMs = maxMs;
        this.rejectMsg = rejectMsg;
        this.defer = makeDefer();
        this.timeout = setTimeout(() => this.defer.reject(new TimeoutError(rejectMsg)), maxMs);
    }
    /**
     * check if runner has failed with error
     *
     * @returns has error flag
     */
    hasError() {
        return this.hasOwnProperty("error");
    }
    /**
     * check if runner has finished with result
     *
     * @returns has result flag
     */
    hasResult() {
        return this.hasOwnProperty("result");
    }
    /**
     * Run tasks
     *
     * @param tasks - Promise or function that returns Promise, or array of them.
     * @returns Promise to wait for tasks to complete, or timeout error.
     */
    async run(tasks) {
        const process = async (x) => (typeof x === "function" ? x() : x);
        // Cast below is due in part to https://github.com/microsoft/TypeScript/issues/17002
        const arrTasks = !Array.isArray(tasks)
            ? process(tasks)
            : Promise.all(tasks.map(process));
        try {
            const r = await Promise.race([arrTasks, this.defer.promise]);
            this.clear();
            this.result = r;
            return r;
        }
        catch (err) {
            this.clear();
            this.error = err;
            throw err;
        }
    }
    /**
     * Cancel the operation and reject with msg
     *
     * @param msg - cancel message
     */
    cancel(msg = "xaa TimeoutRunner operation cancelled") {
        this.clear();
        this.defer.reject(new TimeoutError(msg));
    }
    /** Explicitly clear the setTimeout handle */
    clear() {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }
    /**
     * Check if runner is done
     *
     * @returns is done flag
     */
    isDone() {
        return this.hasResult() || this.hasError();
    }
}
exports.TimeoutRunner = TimeoutRunner;
/**
 * Create a TimeoutRunner to run tasks (promises) with timeout
 *
 * Sample:
 *
 * ```js
 * await xaa.timeout(1000, "timeout fetching data").run(() => fetch(url))
 * ```
 *
 * with promises:
 *
 * ```js
 * await xaa.timout(1000).run([promise1, promise2])
 * ```
 *
 * @param maxMs - number of milliseconds to allow tasks to run
 * @param rejectMsg - message to reject with when timeout triggers
 * @returns TimeoutRunner object
 */
function timeout(maxMs, rejectMsg = "xaa TimeoutRunner operation timed out") {
    return new TimeoutRunner(maxMs, rejectMsg);
}
exports.timeout = timeout;
async function runTimeout(tasks, maxMs, rejectMsg) {
    return await timeout(maxMs, rejectMsg).run(tasks);
}
exports.runTimeout = runTimeout;
/**
 * create map context
 *
 * @param array - array to map
 * @returns map context
 */
function createMapContext(array) {
    return {
        array,
        failed: false,
        assertNoFailure() {
            if (this.failed) {
                throw new Error("assertNoFailure");
            }
        }
    };
}
/**
 * async map for array that supports concurrency
 *
 * Use by xaa.map internally.
 *
 * @param array array to map
 * @param func mapper callback
 * @param options MapOptions
 * @returns promise with mapped result
 */
function multiMap(array, func, options) {
    const awaited = new Array(array.length);
    let error;
    let completedCount = 0;
    let freeSlots = options.concurrency;
    let index = 0;
    const context = createMapContext(array);
    const defer = makeDefer();
    const fail = (err) => {
        context.failed = true;
        if (!error) {
            error = err; // Safe because of the following line:
            error.partial = awaited;
            defer.reject(error);
        }
    };
    const mapNext = () => {
        // important to check this here, so an empty input array immediately
        // gets resolved with an empty result.
        if (!error && completedCount === array.length) {
            return defer.resolve(awaited);
        }
        if (error || freeSlots <= 0 || index >= array.length) {
            return null;
        }
        freeSlots--;
        const pendingIx = index++;
        const save = (x) => {
            completedCount++;
            freeSlots++;
            awaited[pendingIx] = x;
            mapNext();
        };
        try {
            const res = func.call(options.thisArg, array[pendingIx], pendingIx, context);
            if (res && res.then) {
                res.then(save, fail);
                return mapNext();
            }
            else {
                return save(res);
            }
        }
        catch (err) {
            return fail(err);
        }
    };
    //
    // Should not use setTimeout for next:
    //
    // Top level code in async functions before any await statements, execute synchronously.
    //
    // Similar to that setting up promises is sync, and then the
    // fulfilment of them is async, meaning their .then are called.
    //
    mapNext();
    return defer.promise;
}
/**
 * async map array with concurrency
 * - intended to be similar to `bluebird.map`
 *
 * @param array - input array for map
 * @param func - callback to map values from the array
 * @param options - MapOptions
 * @returns promise with mapped result
 */
async function map(array, func, options = { concurrency: 1 }) {
    assert_1.default(Array.isArray(array), `xaa.map expecting an array but got ${typeof array}`);
    if (array.length < 1) {
        return [];
    }
    if (options.concurrency > 1) {
        return multiMap(array, func, options);
    }
    else {
        const awaited = new Array(array.length);
        const context = createMapContext(array);
        for (let i = 0; i < array.length; i++) {
            try {
                awaited[i] = await func.call(options.thisArg, array[i], i, context);
            }
            catch (err) {
                context.failed = true;
                err.partial = awaited;
                throw err;
            }
        }
        return awaited;
    }
}
exports.map = map;
/**
 * async version of array.forEach
 * - iterate through array and await call func with each element and index
 *
 * Sample:
 *
 * ```js
 * await xaa.each([1, 2, 3], async val => await xaa.delay(val))
 * ```
 *
 * @param array array to each
 * @param func callback for each
 */
async function each(array, func) {
    for (let i = 0; i < array.length; i++) {
        await func(array[i], i);
    }
}
exports.each = each;
/**
 * async filter array
 *
 * Sample:
 *
 * ```js
 * await xaa.filter([1, 2, 3], async val => await validateResult(val))
 * ```
 *
 * Beware: concurrency is fixed to 1.
 *
 * @param array array to filter
 * @param func callback for filter
 * @returns filtered result
 */
async function filter(array, func) {
    const filtered = [];
    for (let i = 0; i < array.length; i++) {
        const x = await func(array[i], i);
        if (x)
            filtered.push(array[i]);
    }
    return filtered;
}
exports.filter = filter;
/**
 * try to call and await a function and if it throws, then return `valOrFunc`.
 *
 * - if `valOrFunc` is a function, then return `await valOrFunc(err)`
 *
 * @param func function to try
 * @param valOrFunc value, or callback to get value, to return if `func` throws
 * @returns awaited result, `valOrFunc`, or `await valOrFunc(err)`.
 */
async function tryCatch(func, valOrFunc) {
    try {
        return await func();
    }
    catch (err) {
        return typeof valOrFunc === "function" ? valOrFunc(err) : valOrFunc;
    }
}
exports.tryCatch = tryCatch;
exports.try = tryCatch;
/**
 * Wrap the calling of a function into async/await (promise) context
 * - intended to be similar to `bluebird.try`
 *
 * @param func function to wrap in async context
 * @param args arguments to pass to `func`
 * @returns result from `func`
 */
async function wrap(func, ...args2) {
    return func(...args2);
}
exports.wrap = wrap;
//# sourceMappingURL=xaa.js.map